import React, { useRef, useState } from 'react';
import { useContent } from '../content/ContentContext';
import { api } from '../lib/api';
import { Arrow, Reveal, SectionHeader } from '../lib/ui';

type Status = 'idle' | 'sending' | 'done' | 'error';

const Contact: React.FC = () => {
  const { contact, settings } = useContent();
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState('');
  const startedAt = useRef(Date.now());
  const [form, setForm] = useState({ name: '', email: '', company: '', topic: contact.topics[0] || '', message: '', website: '' });

  if (!contact.visible) return null;

  const update = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');
    setError('');
    try {
      await api.post('/api/contact', { ...form, startedAt: startedAt.current });
      setStatus('done');
    } catch (err: any) {
      setStatus('error');
      setError(err.message || 'Senden fehlgeschlagen. Bitte versuche es per E-Mail.');
    }
  };

  return (
    <section id="kontakt" className="scroll-mt-20 border-t border-line py-24 md:py-32">
      <div className="container-site">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5">
            <SectionHeader eyebrow={contact.eyebrow} title={contact.title} intro={contact.intro} />
            <Reveal delay={0.1}>
              <dl className="mt-10 divide-y divide-line border-y border-line">
                <div className="flex items-baseline justify-between gap-6 py-4">
                  <dt className="text-sm text-fg-3">E-Mail</dt>
                  <dd><a href={`mailto:${settings.email}`} className="text-[15px] font-medium hover:text-accent">{settings.email}</a></dd>
                </div>
                <div className="flex items-baseline justify-between gap-6 py-4">
                  <dt className="text-sm text-fg-3">Telefon &amp; WhatsApp</dt>
                  <dd className="flex gap-4">
                    <a href={`tel:${settings.phone}`} className="text-[15px] font-medium hover:text-accent">{settings.phoneDisplay}</a>
                    {settings.whatsapp && <a href={settings.whatsapp} target="_blank" rel="noopener noreferrer" className="text-[15px] text-fg-2 hover:text-accent">WhatsApp</a>}
                  </dd>
                </div>
                <div className="flex items-baseline justify-between gap-6 py-4">
                  <dt className="text-sm text-fg-3">Adresse</dt>
                  <dd className="text-right text-[15px] text-fg-2">{settings.address.line1}<br />{settings.address.zip} {settings.address.city}</dd>
                </div>
              </dl>
            </Reveal>
          </div>

          <Reveal delay={0.1} className="lg:col-span-7">
            {status === 'done' ? (
              <div className="card flex min-h-[420px] flex-col items-center justify-center p-10 text-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-accent/15">
                  <svg viewBox="0 0 24 24" fill="none" className="h-7 w-7" aria-hidden><path d="M5 12.5l4.5 4.5L19 7.5" stroke="#3fced1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                </div>
                <h3 className="mt-6 text-2xl font-semibold tracking-tight">{contact.successTitle}</h3>
                <p className="mt-2 max-w-sm text-fg-2">{contact.successText}</p>
              </div>
            ) : (
              <form onSubmit={submit} className="card p-6 sm:p-8" noValidate>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="c-name" className="label">Name</label>
                    <input id="c-name" name="name" required autoComplete="name" value={form.name} onChange={update} className="field" placeholder="Dein Name" />
                  </div>
                  <div>
                    <label htmlFor="c-email" className="label">E-Mail</label>
                    <input id="c-email" name="email" type="email" required autoComplete="email" value={form.email} onChange={update} className="field" placeholder="du@firma.de" />
                  </div>
                  <div>
                    <label htmlFor="c-company" className="label">Firma <span className="text-fg-3">(optional)</span></label>
                    <input id="c-company" name="company" autoComplete="organization" value={form.company} onChange={update} className="field" placeholder="Firma oder Shop" />
                  </div>
                  <div>
                    <label htmlFor="c-topic" className="label">Thema</label>
                    <select id="c-topic" name="topic" value={form.topic} onChange={update} className="field appearance-none">
                      {contact.topics.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="c-message" className="label">Nachricht</label>
                    <textarea id="c-message" name="message" required rows={5} value={form.message} onChange={update} className="field resize-y" placeholder="Worum geht es?" />
                  </div>
                  {/* Honeypot: bleibt für Menschen unsichtbar */}
                  <div className="absolute -left-[9999px] top-0" aria-hidden>
                    <label>Website<input name="website" tabIndex={-1} autoComplete="off" value={form.website} onChange={update} /></label>
                  </div>
                </div>
                {status === 'error' && <p role="alert" className="mt-4 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</p>}
                <div className="mt-6 flex flex-col-reverse gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-xs leading-relaxed text-fg-3">{contact.note}</p>
                  <button type="submit" disabled={status === 'sending'} className="btn-primary shrink-0 disabled:opacity-60">
                    {status === 'sending' ? 'Wird gesendet…' : 'Nachricht senden'} <Arrow />
                  </button>
                </div>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
